import axios from 'axios'
import * as Sentry from '@sentry/vue'
import { enrichAxiosTimeoutError } from '../utils/axiosTimeoutContext'
import {
  endImpersonationSession,
  isExpectedSessionEndError,
  isImpersonationAccessToken,
  markExpectedSessionEnd,
  markRefreshFailureHandled,
} from '../utils/impersonationSession'
import { useToast } from '../composables/useToast'
import { clearUserLocalStorage } from '../utils/userLocalStorage'
import {
  persistLogoutReason,
  logoutReasonFromRefreshError,
  markMissingRefreshToken,
  shouldKeepChapterBackups,
  type LogoutReason,
} from '../utils/logoutReason'
import { clearSentryUser } from '../utils/sentryUser'
import { beginSessionEnd } from '../utils/sessionLifecycle'

let refreshPromise: Promise<void> | null = null
let pendingRequests: Array<{
  resolve: (token: string) => void
  reject: (error: any) => void
}> = []

const REFRESH_RETRY_DELAYS = [500, 1000, 2000]
const MAX_REFRESH_RETRIES = REFRESH_RETRY_DELAYS.length // 3 retries after initial attempt = 4 total calls

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

function isTransientError(error: any): boolean {
  if (!error.response) {
    return axios.isAxiosError(error)
  }
  const status = error.response.status
  return status === 408 || status === 429 || status >= 500
}

function isAuthError(error: any): boolean {
  if (!error.response) {
    return false
  }
  const status = error.response.status
  return status === 401 || status === 403
}

function enrichAxios4xxError(error: any): any {
  if (!error?.response) return error

  const { response } = error
  const status = response.status

  if (status < 400 || status >= 500) return error

  const config = error.config || {}
  const url = config.url || ''

  if (!url.includes(import.meta.env.VITE_API_URL)) return error

  const enrichedData: Record<string, any> = {
    status,
    responseBody: response.data,
    url,
  }

  if (config.params) {
    const sanitizedParams = { ...config.params }
    delete sanitizedParams.token
    delete sanitizedParams.password
    delete sanitizedParams.accessToken
    delete sanitizedParams.refreshToken
    enrichedData.queryParams = sanitizedParams
  }

  error.escribonauta4xxContext = enrichedData

  return error
}

function endSessionGracefully(reason: LogoutReason = 'revoked') {
  const accessToken = localStorage.getItem('accessToken')
  if (isImpersonationAccessToken(accessToken)) {
    endImpersonationSession()
    return
  }

  const keepChapterBackups = shouldKeepChapterBackups(reason)
  persistLogoutReason(reason)
  beginSessionEnd({ flushBackups: keepChapterBackups })
  clearSentryUser()
  clearUserLocalStorage({ keepChapterBackups })
  window.location.href = '/'
}

async function attemptRefreshWithRetries(): Promise<{ access: string; refresh: string }> {
  const refreshToken = localStorage.getItem('refreshToken')
  if (!refreshToken) {
    const error = new Error('No refresh token available')
    markExpectedSessionEnd(error)
    markMissingRefreshToken(error)
    throw error
  }

  let lastError: any = null

  for (let attempt = 0; attempt <= MAX_REFRESH_RETRIES; attempt++) {
    try {
      const response = await axios.post(`${import.meta.env.VITE_API_URL}/v1/auth/refresh`, {
        refreshToken,
      })

      if (response.status === 200) {
        const { access, refresh } = response.data.data
        return { access, refresh }
      }
    } catch (error: any) {
      lastError = error

      if (isAuthError(error)) {
        markExpectedSessionEnd(error)
        throw error
      }

      if (isTransientError(error) && attempt < MAX_REFRESH_RETRIES) {
        await sleep(REFRESH_RETRY_DELAYS[attempt])
        continue
      }

      break
    }
  }

  throw lastError || new Error('Refresh token failed after retries')
}

async function executeSharedRefresh(): Promise<void> {
  try {
    const { access, refresh } = await attemptRefreshWithRetries()
    localStorage.setItem('accessToken', access)
    localStorage.setItem('refreshToken', refresh)
    axios.defaults.headers.common.Authorization = `Bearer ${access}`

    pendingRequests.forEach((req) => req.resolve(access))
    pendingRequests = []
  } catch (error: any) {
    if (isAuthError(error) || isExpectedSessionEndError(error)) {
      endSessionGracefully(logoutReasonFromRefreshError(error))
      pendingRequests.forEach((req) => {
        markExpectedSessionEnd(error)
        req.reject(error)
      })
      pendingRequests = []
    } else {
      if (!isTransientError(error)) {
        Sentry.captureException(error)
      }
      showRefreshFailedToast()
      pendingRequests.forEach((req) => {
        markRefreshFailureHandled(error)
        req.reject(error)
      })
      pendingRequests = []
    }
    throw error
  } finally {
    refreshPromise = null
  }
}

function triggerRefresh(): Promise<void> {
  if (!refreshPromise) {
    refreshPromise = executeSharedRefresh()
  }
  return refreshPromise
}

function showRefreshFailedToast() {
  const { triggerToast } = useToast()
  triggerToast({
    message: 'No se pudo renovar la sesión debido a problemas de conexión. Intenta nuevamente en breve.',
    color: 'warning',
    timeout: 10000,
    action: {
      label: 'Reintentar',
      handler: () => {
        void triggerRefresh().catch(() => {})
      },
    },
  })
}

function refreshAccessToken(request: any): Promise<any> {
  const refreshToken = localStorage.getItem('refreshToken')
  if (!refreshToken) {
    endSessionGracefully('missing_token')
    return new Promise(() => {})
  }

  triggerRefresh().catch(() => {})

  return new Promise((resolve, reject) => {
    pendingRequests.push({
      resolve: (newToken: string) => {
        request.headers.Authorization = `Bearer ${newToken}`
        resolve(axios(request))
      },
      reject: (error: any) => {
        reject(error)
      },
    })
  })
}

function setupRequestInterceptor() {
  axios.interceptors.request.use(
    (config: any) => {
      const token = localStorage.getItem('accessToken')
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
      if (!config.url.includes(import.meta.env.VITE_API_URL)) {
        const { headers } = config
        if (headers && headers.Authorization) {
          delete headers.Authorization
        }
        if (headers && headers.common && headers.common.Authorization) {
          delete headers.common.Authorization
        }
      }
      return config
    },
    (error: any) => Promise.reject(error),
  )
}

function setupRefreshInterceptor() {
  const isEscribonautaApi = (url: string) => url.includes(import.meta.env.VITE_API_URL)

  axios.interceptors.response.use(
    (response: any) => response,
    (error: any) => {
      let enrichedError = enrichAxiosTimeoutError(error)
      enrichedError = enrichAxios4xxError(enrichedError)
      const request = enrichedError?.config
      if (!request?.url) {
        return Promise.reject(enrichedError)
      }
      if (request.url === `${import.meta.env.VITE_API_URL}/v1/auth/refresh`) {
        if (isAuthError(enrichedError)) {
          markExpectedSessionEnd(enrichedError)
        }
        return Promise.reject(enrichedError)
      }
      if (!isEscribonautaApi(request.url)) {
        return Promise.reject(enrichedError)
      }
      if (enrichedError.response?.status === 401 && !request.retry) {
        const refreshToken = localStorage.getItem('refreshToken')
        const accessToken = localStorage.getItem('accessToken')
        if (!refreshToken || isImpersonationAccessToken(accessToken)) {
          markExpectedSessionEnd(enrichedError)
          endSessionGracefully(refreshToken ? 'revoked' : 'missing_token')
          return new Promise(() => {})
        }
        request.retry = true
        return refreshAccessToken(request)
      }
      return Promise.reject(enrichedError)
    },
  )
}

function initializeInterceptors() {
  setupRequestInterceptor()
  setupRefreshInterceptor()
}

export { isExpectedSessionEndError }
export default initializeInterceptors
